import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Info, RotateCcw, Ruler } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeBraSize,
  validate,
  FIT_SUGGESTIONS,
  type SizingResult,
  type Unit,
} from "@/lib/calculators/braSize";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Bra Size Calculator", bn: "ব্রা সাইজ ক্যালকুলেটর" },
  desc: {
    en: "Calculate your bra size in US, UK and EU/International systems. Includes sister sizes and a fit guide. Information only — try on for actual fit.",
    bn: "US, UK ও EU/আন্তর্জাতিক সিস্টেমে ব্রা সাইজ হিসাব করুন। সিস্টার সাইজ ও ফিট গাইড সহ। শুধু তথ্যমূলক — প্রকৃত ফিটের জন্য পরে দেখুন।",
  },
  unit: { en: "Units", bn: "একক" },
  underbust: { en: "Underbust (band)", bn: "আন্ডারবাস্ট (ব্যান্ড)" },
  underbustHint: {
    en: "Snugly under the bust where the band sits.",
    bn: "বাস্টের ঠিক নিচে যেখানে ব্যান্ড বসে — শক্ত করে মাপুন।",
  },
  bust: { en: "Bust (fullest)", bn: "বাস্ট (সবচেয়ে চওড়া)" },
  bustHint: {
    en: "Across the fullest part of the bust, tape parallel to the floor.",
    bn: "বাস্টের সবচেয়ে চওড়া অংশে, টেপ মেঝের সমান্তরাল।",
  },
  reset: { en: "Reset", bn: "রিসেট" },
  yourSize: { en: "Your Bra Size", bn: "আপনার ব্রা সাইজ" },
  conversion: { en: "Conversion across systems", bn: "সিস্টেম অনুযায়ী রূপান্তর" },
  sisterSizes: { en: "Sister sizes (similar volume)", bn: "সিস্টার সাইজ (একই ভলিউম)" },
  sisterHint: {
    en: "Try these if your exact size is not available — they have similar cup volume.",
    bn: "আপনার সঠিক সাইজ না পেলে এগুলো চেষ্টা করুন — কাপ ভলিউম প্রায় একই।",
  },
  fitIssues: { en: "Common fit issues & solutions", bn: "সাধারণ ফিট সমস্যা ও সমাধান" },
  enterMeasurements: { en: "Enter underbust and bust to see your size.", bn: "সাইজ দেখতে আন্ডারবাস্ট ও বাস্টের মাপ দিন।" },
  disclaimer: {
    en: "Bra fit varies significantly by brand, style and material. This calculator is a starting point, not a final fitting. Always try on before buying when possible. A poorly fitting bra can cause back, shoulder and posture problems — consult a professional fitter for persistent fit issues.",
    bn: "ব্রা ফিট ব্র্যান্ড, স্টাইল ও কাপড় অনুযায়ী অনেক ভিন্ন হতে পারে। এই ক্যালকুলেটর শুরু করার একটি মাত্র জায়গা — চূড়ান্ত মাপ নয়। সম্ভব হলে কেনার আগে পরে দেখুন। ভুল মাপের ব্রা পিঠ, কাঁধ ও ভঙ্গিতে সমস্যা সৃষ্টি করতে পারে — দীর্ঘস্থায়ী সমস্যায় পেশাদারের পরামর্শ নিন।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How do I measure my bra size?", bn: "ব্রা সাইজ কীভাবে মাপব?" },
    a: {
      en: "Stand straight, wear a non-padded bra (or none). Measure underbust snugly where the band would sit, parallel to the floor. Measure bust across the fullest part, tape comfortably loose. Enter both numbers above.",
      bn: "সোজা দাঁড়িয়ে, নন-প্যাডেড ব্রা পরে (বা ছাড়া)। যেখানে ব্যান্ড বসবে সেখানে শক্ত করে আন্ডারবাস্ট মাপুন (মেঝের সমান্তরাল)। বাস্টের সবচেয়ে চওড়া অংশে আরাম করে মাপুন। উপরে দুটি সংখ্যা দিন।",
    },
  },
  {
    q: { en: "What is a sister size?", bn: "সিস্টার সাইজ কী?" },
    a: {
      en: "Sister sizes have the same cup volume but a different band. If your size is 34C, sister sizes are 32D and 36B. Useful when your exact size is unavailable — try a sister size up if the band feels tight, or down if it feels loose.",
      bn: "সিস্টার সাইজে কাপের ভলিউম একই কিন্তু ব্যান্ড আলাদা। ৩৪C-এর সিস্টার সাইজ ৩২D ও ৩৬B। সঠিক সাইজ না পেলে — ব্যান্ড টাইট হলে আপ, ঢিলা হলে ডাউন সিস্টার সাইজ চেষ্টা করুন।",
    },
  },
  {
    q: { en: "US vs UK bra size — what's the difference?", bn: "US ও UK ব্রা সাইজের পার্থক্য?" },
    a: {
      en: "Band numbers are the same. Cup letters match through DD, then UK uses E, F, FF, G, GG, while US continues DDD, DDDD or jumps to F, G. So US 34DDD ≈ UK 34E.",
      bn: "ব্যান্ড সংখ্যা একই। DD পর্যন্ত কাপ একই, এরপর UK ব্যবহার করে E, F, FF, G, GG, আর US ব্যবহার করে DDD, DDDD বা F, G। তাই US ৩৪DDD ≈ UK ৩৪E।",
    },
  },
  {
    q: { en: "How does EU/International sizing work?", bn: "EU/আন্তর্জাতিক সাইজ কীভাবে কাজ করে?" },
    a: {
      en: "EU bands are in centimetres (60, 65, 70, 75, 80…) — your underbust rounded to the nearest 5 cm. Cup is based on the difference (bust − band) in cm: every 2 cm = one cup up.",
      bn: "EU ব্যান্ড সেন্টিমিটারে (৬০, ৬৫, ৭০, ৭৫, ৮০…) — আপনার আন্ডারবাস্ট ৫ সেমির গুণিতকে রাউন্ড। কাপ = (বাস্ট − ব্যান্ড) সেমিতে; প্রতি ২ সেমি = এক কাপ বড়।",
    },
  },
  {
    q: { en: "How accurate is this calculator?", bn: "এই ক্যালকুলেটর কতটা নির্ভুল?" },
    a: {
      en: "It gives a starting point based on standard sizing methods. Actual fit depends on the brand, style (balconette, full coverage, sports), breast shape and material. Always try on when you can.",
      bn: "এটি স্ট্যান্ডার্ড পদ্ধতি অনুযায়ী শুরুর মাপ দেয়। আসল ফিট ব্র্যান্ড, স্টাইল (বালকনেট, ফুল কভারেজ, স্পোর্টস), স্তনের আকৃতি ও কাপড়ের উপর নির্ভর করে। সম্ভব হলে পরে দেখুন।",
    },
  },
  {
    q: { en: "ব্রা সাইজ কীভাবে মাপা হয়?", bn: "ব্রা সাইজ কীভাবে মাপা হয়?" },
    a: {
      en: "আন্ডারবাস্ট (ব্যান্ড) ও বাস্টের সবচেয়ে চওড়া অংশ — দুটি মাপ নিন। ব্যান্ড সংখ্যা আন্ডারবাস্ট থেকে, কাপ আসে পার্থক্য (বাস্ট − ব্যান্ড) থেকে।",
      bn: "আন্ডারবাস্ট (ব্যান্ড) ও বাস্টের সবচেয়ে চওড়া অংশ — দুটি মাপ নিন। ব্যান্ড সংখ্যা আন্ডারবাস্ট থেকে, কাপ আসে পার্থক্য (বাস্ট − ব্যান্ড) থেকে।",
    },
  },
];

export const Route = createFileRoute("/bra-size-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Bra Size Calculator — US, UK, EU Sizing | Bangla Calculator",
      description:
        "Free bra size calculator. Calculate bra size in US, UK, EU/International systems. Sister sizes and fit guide for Bangladesh. ব্রা সাইজ ক্যালকুলেটর।",
      path: "/bra-size-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Bra Size Calculator", path: "/bra-size-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BraSizePage,
});

function BraSizePage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 1) => num(n.toFixed(d));

  const [unit, setUnit] = useState<Unit>("cm");
  const [underbust, setUnderbust] = useState("75");
  const [bust, setBust] = useState("87");

  const inp = {
    underbust: parseFloat(underbust),
    bust: parseFloat(bust),
    unit,
  };

  const result: SizingResult | null = useMemo(() => {
    const v = validate(inp);
    if (!v.ok) return null;
    return computeBraSize(inp);
  }, [unit, underbust, bust]);

  const reset = () => {
    setUnit("cm");
    setUnderbust("75");
    setBust("87");
  };

  const shareLine = result ? `${tr("yourSize")}: ${result.usSize} (US) · ${result.ukSize} (UK) · ${result.euSize} (EU)` : "";

  return (
    <CalculatorLayout
      slug="bra-size-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Ruler"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Unit toggle */}
      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("unit")}</label>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {(["cm", "in"] as Unit[]).map((u) => {
            const active = u === unit;
            return (
              <button
                key={u}
                type="button"
                onClick={() => setUnit(u)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition",
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {u === "cm" ? "cm" : "inches"}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <NumberInput label={tr("underbust")} value={underbust} onChange={setUnderbust} unit={unit} />
          <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("underbustHint")}</p>
        </div>
        <div>
          <NumberInput label={tr("bust")} value={bust} onChange={setBust} unit={unit} />
          <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("bustHint")}</p>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {!result && (
        <p className={cn("mt-6 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("enterMeasurements")}</p>
      )}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary */}
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("yourSize")}
                </p>
                <p className="mt-1 flex items-center gap-2 text-foreground">
                  <Ruler size={22} className="text-accent" />
                  <span className="text-2xl font-semibold md:text-3xl tabular-nums">
                    {num(result.usBand)}{result.usCup}
                  </span>
                  <span className="text-xs text-muted-foreground">US</span>
                </p>
                <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  <span className="tabular-nums text-foreground">{num(result.ukBand)}{result.ukCup}</span> UK ·{" "}
                  <span className="tabular-nums text-foreground">{num(result.euBand)}{result.euCup}</span> EU
                </p>
                <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {lang === "bn" ? "পার্থক্য" : "Difference"}:{" "}
                  <span className="tabular-nums text-foreground">{fmt(result.diffIn, 1)}″ / {fmt(result.diffCm, 1)} cm</span>
                </p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>
          </div>

          {/* Conversion table */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("conversion")}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">US</th>
                    <th className="py-2 pr-4 font-medium">UK</th>
                    <th className="py-2 font-medium">EU / Intl</th>
                  </tr>
                </thead>
                <tbody>
                  {result.conversionTable.map((row, i) => {
                    const isMain = row.us === result.usSize;
                    return (
                      <tr key={i} className={cn("border-b border-border/50 last:border-0", isMain && "bg-accent/10")}>
                        <td className={cn("py-2 pr-4 tabular-nums", isMain && "font-semibold text-accent")}>{row.us}</td>
                        <td className={cn("py-2 pr-4 tabular-nums", isMain && "font-semibold text-accent")}>{row.uk}</td>
                        <td className={cn("py-2 tabular-nums", isMain && "font-semibold text-accent")}>{row.eu}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sister sizes */}
          {result.sisterSizes.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className={cn("mb-2 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("sisterSizes")}</h3>
              <p className={cn("mb-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("sisterHint")}</p>
              <div className="flex flex-wrap gap-2">
                {result.sisterSizes.map((s) => (
                  <span key={s.size} className="inline-flex items-center rounded-md border border-border bg-muted/40 px-3 py-1 text-sm font-medium tabular-nums text-foreground">
                    {s.size}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Fit issues */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("fitIssues")}</h3>
            <ul className="space-y-2">
              {FIT_SUGGESTIONS.map((f) => (
                <li key={f.issue} className={cn("rounded-lg border border-border/60 p-3 text-xs", lang === "bn" && "font-bn")}>
                  <p className="font-medium text-foreground">{f.problem[lang]}</p>
                  <p className="mt-1 text-muted-foreground">→ {f.solution[lang]}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Disclaimer */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs">
            <Link to="/body-shape-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Body Shape <ArrowRight size={12} /></Link>
            <Link to="/unit-converter" className="inline-flex items-center gap-1 text-accent hover:underline">Unit Converter <ArrowRight size={12} /></Link>
            <Link to="/cm-to-feet" className="inline-flex items-center gap-1 text-accent hover:underline">CM to Feet <ArrowRight size={12} /></Link>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "সঠিকভাবে কীভাবে মাপবেন" : "How to measure correctly"} lang={lang}>
          <ol className={cn("list-decimal space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "নন-প্যাডেড ব্রা পরে (অথবা ছাড়া) সোজা দাঁড়ান।" : "Stand straight wearing a non-padded bra (or none)."}</li>
            <li>{lang === "bn" ? "আন্ডারবাস্ট: যেখানে ব্যান্ড বসবে সেখানে শক্ত করে মাপুন — মেঝের সমান্তরাল।" : "Underbust: measure snugly where the band sits, parallel to the floor."}</li>
            <li>{lang === "bn" ? "বাস্ট: সবচেয়ে চওড়া অংশে আরাম করে মাপুন — চাপ দেবেন না।" : "Bust: measure across the fullest part — comfortably loose, do not compress."}</li>
            <li>{lang === "bn" ? "দিনের বিভিন্ন সময়ে শরীর সামান্য বদলায় — সকালে সবচেয়ে ভালো ফলাফল।" : "Body changes slightly through the day — morning gives the most reliable reading."}</li>
          </ol>
        </Section>

        <Section title={lang === "bn" ? "ব্যান্ড সাইজ" : "Band size"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ব্যান্ড সাইজ আপনার আন্ডারবাস্টের পরিধি। US/UK ইঞ্চিতে নিকটতম জোড় সংখ্যায় (snug method) রাউন্ড করে। EU সরাসরি সেন্টিমিটার, ৫-এর গুণিতকে রাউন্ড।"
              : "Band size is the circumference of your underbust. US/UK round to the nearest even inch (the modern 'snug' method). EU uses centimetres rounded to the nearest 5."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "কাপ সাইজ" : "Cup size"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "কাপ সাইজ আসে পার্থক্য থেকে: বাস্ট − ব্যান্ড। প্রতি ১ ইঞ্চি (US/UK) বা ২ সেমি (EU) = এক কাপ বড়। তাই ১\" → A, ২\" → B, ৩\" → C ইত্যাদি।"
              : "Cup size comes from the difference: bust − band. Each 1 inch (US/UK) or 2 cm (EU) equals one cup up. So 1\" → A, 2\" → B, 3\" → C, and so on."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "US বনাম UK বনাম EU সাইজ" : "US vs UK vs EU sizing"} lang={lang}>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">{lang === "bn" ? "পার্থক্য" : "Diff"}</th>
                  <th className="px-3 py-2 font-medium">US</th>
                  <th className="px-3 py-2 font-medium">UK</th>
                  <th className="px-3 py-2 font-medium">EU</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["1\" / 2 cm", "A", "A", "A"],
                  ["2\" / 4 cm", "B", "B", "B"],
                  ["3\" / 6 cm", "C", "C", "C"],
                  ["4\" / 8 cm", "D", "D", "D"],
                  ["5\" / 10 cm", "DD", "DD", "E"],
                  ["6\" / 12 cm", "DDD", "E", "F"],
                  ["7\" / 14 cm", "DDDD/F", "F", "G"],
                  ["8\" / 16 cm", "G", "FF", "H"],
                ].map((r, i) => (
                  <tr key={i} className="border-b border-border/40 last:border-0">
                    {r.map((c, j) => <td key={j} className="px-3 py-2 tabular-nums">{c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "bn" ? "সিস্টার সাইজ" : "Sister sizes"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "সিস্টার সাইজে কাপের ভলিউম একই কিন্তু ব্যান্ড আলাদা। উদাহরণ: ৩৪C-এর সিস্টার সাইজ ৩২D ও ৩৬B। নিয়ম: ব্যান্ড ছোট করলে কাপ এক বড় করুন; ব্যান্ড বড় করলে কাপ এক ছোট করুন।"
              : "Sister sizes share cup volume but use a different band. Example: 34C's sisters are 32D and 36B. Rule: go down a band → up a cup; go up a band → down a cup."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "বাংলাদেশের ক্রেতাদের জন্য" : "For shoppers in Bangladesh"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "বাংলাদেশে আমদানি করা ব্রা মূলত তিনটি সিস্টেমে আসে: US (Hanes, Maidenform, Victoria's Secret), UK (Marks & Spencer, Debenhams) এবং EU/আন্তর্জাতিক (চীনা ও ইউরোপীয় ব্র্যান্ড)। লেবেলে কোন সিস্টেম তা দেখে উপরের রূপান্তর টেবিল মিলিয়ে নিন।"
              : "Bras sold in Bangladesh arrive in three main systems: US (Hanes, Maidenform, Victoria's Secret), UK (Marks & Spencer, Debenhams) and EU/International (Chinese and European brands). Check which system the label uses and match it against the conversion table above."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "কেন সঠিক ফিট গুরুত্বপূর্ণ" : "Why correct fit matters"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ভুল মাপের ব্রা পিঠ ও কাঁধে ব্যথা, ভঙ্গির সমস্যা ও ত্বকে দাগ ফেলতে পারে। সমর্থন আসে ৮০% ব্যান্ড থেকে, ২০% স্ট্র্যাপ থেকে — তাই ব্যান্ড ফিট সবচেয়ে গুরুত্বপূর্ণ।"
              : "Poor bra fit can cause back and shoulder pain, posture issues and skin marks. About 80% of support comes from the band and 20% from the straps — so band fit is the most important factor."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <div className={cn("flex items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>{tr("disclaimer")}</p>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
